import { SerializedStyles } from "@emotion/react";
import { ReactNode, Ref, memo } from "react";
import { useFullSubmit } from "src/apps/AppFlow/pages/hooks";
import { ApplicationFlowState } from "src/apps/AppFlow/screens/root/Layout";
import {
  AgreementParagraph,
  CreditAuthorization,
  FormFlowPage,
  InfoAlert,
  ScrollablePDFViewer,
} from "src/components";
import { LightText, RegularText } from "src/components/v1";
import config from "src/config";
import { selectApplicationLocation } from "src/selectors";
import { createStyles } from "src/styles";
import { BlockFocus } from "src/utilities/react";

import { colors, types } from "@fraction/shared";

export interface ImportantTermsAndInfoProps {
  focusRef?: Ref<HTMLDivElement>;
  blockFocus?: BlockFocus;
  onNext: () => void;
  onBack: () => void;
  style?: SerializedStyles;
  value: ApplicationFlowState;
  children?: ReactNode;
  termLength?: number;
  location?: types.ExtractedPropertyAddress;
}

const styles = createStyles({
  container: {
    paddingBottom: 160,
  },
  textContainerStyle: {
    marginBottom: 48,
  },
  section: {
    marginBottom: 20,
  },
  subheader: {
    fontSize: 24,
    color: colors.palette.GREEN,
  },
  description: {
    fontSize: 18,
    margin: "24px 0",
  },
  alert: {
    marginBottom: 40,
  },
  agreement: {
    marginBottom: 20,
  },
  pdf: {
    height: 350,
    "@media(max-width: 600px)": {
      height: 250,
    },
  },
});

const PdfSection = ({ header, subheader, url }: { header: string; subheader: string; url: string }) => (
  <div css={styles.section}>
    <RegularText style={styles.subheader}>{header}</RegularText>
    <LightText style={styles.description}>{subheader}</LightText>
    <ScrollablePDFViewer
      style={styles.pdf}
      file={{
        url,
      }}
    />
  </div>
);

const ImportantTermsAndInfo = ({
  onNext,
  onBack,
  style,
  focusRef,
  value,
  children,
  termLength = 5,
  location,
  ...props
}: ImportantTermsAndInfoProps) => {
  const { handleNext, nextDisabled, nextLoading } = useFullSubmit();
  const { isColorado } = selectApplicationLocation(location);

  return (
    <FormFlowPage
      {...props}
      title="Important terms and information"
      buttonText="Submit application"
      overflowTextFallback="Submit"
      onNext={handleNext}
      onBack={onBack}
      style={[styles.container, style]}
      nextDisabled={nextDisabled}
      nextLoading={nextLoading}
      ref={focusRef}
      textContainerStyle={styles.textContainerStyle}
      isStickyDesktopFooter
    >
      <PdfSection
        header="HELOC Booklet"
        subheader="What you should know about home equity lines of credit."
        url={`${config.s3URL.legalDocs}/WA/Heloc-booklet.pdf`}
      />
      <PdfSection
        header="TILA Terms"
        subheader="Important terms of the Fraction appreciation home equity line of credit."
        // NOTE - we only are showing the 5 year term now, so only posting the 5 year term doc
        url="https://api.fraction.com/document/dl/tila-important-terms-18mo/washington"
      />
      {isColorado && (
        <InfoAlert style={styles.alert}>
          An additional charge may be made for insurance written in connection with your loan transaction
          against loss of or damage to property or against liability. You may choose the person through whom
          the insurance is to be obtained.
        </InfoAlert>
      )}
      <AgreementParagraph style={styles.agreement}>
        <CreditAuthorization />
      </AgreementParagraph>
      {children}
    </FormFlowPage>
  );
};

export default memo(ImportantTermsAndInfo);
